// Commitment contracts (COM-*, COV-*).
//
// A Commitment has NO WINDOW FIELDS (COM-014, invariant 5). It covers the need's
// window by definition. Adding start/end here would let a commitment drift from the
// need it covers, which is precisely the bug the kernel's shape prevents — so the
// absence below is deliberate, not an omission.
//
// It BINDS the revision it agreed to (`needRevision`). If the need changes
// materially the commitment goes to "Please recheck" and the helper is asked again;
// nobody is ever silently re-consented (invariant 4).

import { z } from "zod";

import type { CommitmentRejection, Result } from "../errors.js";
import {
  circleIdSchema,
  commitmentIdSchema,
  personIdSchema,
  revisionSchema,
  supportNeedIdSchema,
} from "../primitives.js";

export const acceptNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  /**
   * The revision the helper was actually looking at when they agreed. Required, not
   * optional: without it the server cannot tell consent from a stale click, and
   * invariant 4 becomes unenforceable.
   */
  needRevision: revisionSchema,
  /**
   * Retry-safe. Two taps on a flaky train connection must not produce two
   * commitments, and the loser of a genuine race must get `capacity_full` rather
   * than a duplicate.
   */
  idempotencyKey: z.string().min(8).max(128),
});

/**
 * Withdraw from your own commitment (COM-010, COM-012).
 *
 * `reasonCode`, not free text, and never required — §10.10 is explicit that
 * withdrawing must not demand an excuse, and COM-013 that the copy stays neutral.
 *
 * A CODE rather than a sentence because the plan's own schema names the stored field
 * `withdrawalReasonCode` (§28), and because a code is the only shape that can satisfy
 * COM-012 and NFR-OBS-002 at once: an organiser-private reason still has to survive
 * being written to an audit row, and §16.18 keeps free text out of exactly those
 * places. The words a helper reads belong to `@herefor/product-language`.
 *
 * The 2026-08-30 ruling on bead `hf-pp4` fixes six circumstance-shaped codes and
 * explicitly excludes V1 free text. `other` is complete without elaboration.
 */
export const withdrawalReasonCodeSchema = z.enum([
  "unwell",
  "family_change",
  "schedule_clash",
  "transport",
  "no_longer_available",
  "other",
]);

export const withdrawCommitmentInput = z.object({
  circleId: circleIdSchema,
  commitmentId: commitmentIdSchema,
  reasonCode: withdrawalReasonCodeSchema.optional(),
});

/**
 * A renewed decision after a material change (COM-015, COM-016, plan §10.9).
 *
 * TWO VALUES, AND NEITHER OF THEM IS "acknowledged". Plan §10.9 is unusually blunt:
 * "A generic Acknowledge button is insufficient. The person must make a renewed
 * commitment decision." A third value here — seen, dismissed, snoozed — would let a
 * commitment drift back into counting without anybody agreeing to the new
 * arrangement, which is exactly what invariant 4 forbids.
 *
 * The spelling is the kernel's `ReconfirmationDecision`, deliberately, so the wire
 * and the domain cannot describe the same answer with two different words.
 */
export const reconfirmationDecisionSchema = z.enum(["yes_still_can", "no_cannot"]);

export const reconfirmCommitmentInput = z.object({
  circleId: circleIdSchema,
  commitmentId: commitmentIdSchema,
  /** The NEW revision being agreed to. Re-consent is explicit, by construction. */
  needRevision: revisionSchema,
  /**
   * Yes or No — never absent, never defaulted.
   *
   * "No" is carried HERE rather than sent to `withdraw` because the two are
   * different events in a plan's history, and an organiser reading it needs them
   * apart: somebody who stepped back unprompted made a choice about their week,
   * while somebody who declined after a change was asked a question they did not
   * ask for and answered it honestly. Collapsing them would make the second look
   * like the first.
   */
  decision: reconfirmationDecisionSchema,
  idempotencyKey: z.string().min(8).max(128),
});

/** No arguments: "mine" is the caller, resolved at the boundary, never passed in. */
export const myRechecksInput = z.object({
  circleId: circleIdSchema,
});

/*
 * Answering a direct request lives in `./requests.js`, not here.
 *
 * A request is a QUESTION and a commitment is a PROMISE — the domain model keeps the
 * two apart deliberately (plan §9.8), and so does this package. The one thing the
 * two modules share is `CommitmentAccepted`: accepting an ask creates an ordinary
 * commitment through the same claim guard, so the success shape is the same one
 * `volunteer` returns rather than a second spelling of it.
 */

/** The three explicit V1 ways somebody may agree outside Here For (COM-008). */
export const proxyAgreementSourceSchema = z.enum(["whatsapp", "phone", "in_person"]);

/** Recording that someone agreed offline. Attributed, never anonymous (COM-007–009). */
export const recordProxyCommitmentInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  responsiblePersonId: personIdSchema,
  needRevision: revisionSchema,
  /** How the agreement was obtained, so the record is auditable rather than asserted. */
  agreementSource: proxyAgreementSourceSchema,
  /** The exact consequence was shown and deliberately confirmed; never defaulted. */
  consentConfirmed: z.literal(true),
  idempotencyKey: z.string().min(8).max(128),
});

/**
 * Answering a recheck ON SOMEBODY ELSE'S BEHALF (bead hf-ww3).
 *
 * The same three fields the self answer takes, plus the two that make it a PROXY
 * record — and the reason those two are required rather than inherited is the whole
 * design of this input.
 *
 * A material change makes a commitment ask its question again. The offline member who
 * agreed by phone is counted toward coverage (COV-009 puts `offline` alongside
 * `active`), is therefore flipped by the cascade, and is by definition the one person
 * who will never open the app to answer. Without this, the need sits on "Please
 * recheck" forever and the only escape is withdrawing them, which records something
 * that did not happen.
 *
 * `agreementSource` is NEW every time, and never the one on the existing row. The
 * original source describes a conversation that happened BEFORE the change existed;
 * reusing it would make the audit trail say somebody re-agreed to a new time in a
 * phone call that predates it. Re-consent is a fresh act or it is not consent.
 */
export const reconfirmProxyCommitmentInput = z.object({
  circleId: circleIdSchema,
  commitmentId: commitmentIdSchema,
  /** The NEW revision being agreed to on their behalf. */
  needRevision: revisionSchema,
  decision: reconfirmationDecisionSchema,
  /** How THIS answer was obtained — never inherited from the original record. */
  agreementSource: proxyAgreementSourceSchema,
  /** The exact consequence was shown and deliberately confirmed; never defaulted. */
  consentConfirmed: z.literal(true),
  idempotencyKey: z.string().min(8).max(128),
});

export type AcceptNeedInput = z.input<typeof acceptNeedInput>;
export type WithdrawalReasonCode = z.infer<typeof withdrawalReasonCodeSchema>;
export type WithdrawCommitmentInput = z.input<typeof withdrawCommitmentInput>;
export type ReconfirmationDecision = z.infer<typeof reconfirmationDecisionSchema>;
export type ReconfirmCommitmentInput = z.input<typeof reconfirmCommitmentInput>;
export type ReconfirmProxyCommitmentInput = z.input<typeof reconfirmProxyCommitmentInput>;
export type MyRechecksInput = z.input<typeof myRechecksInput>;
export type ProxyAgreementSource = z.infer<typeof proxyAgreementSourceSchema>;
export type RecordProxyCommitmentInput = z.input<typeof recordProxyCommitmentInput>;

export interface CommitmentAccepted {
  readonly commitmentId: string;
  readonly boundNeedRevision: number;
  /** Coverage AFTER this acceptance, as a count — derived, never stored. */
  readonly confirmedCapacity: number;
  readonly requiredCapacity: number;
}

/** One of the caller's own commitments, as the "what have I said yes to" list needs it. */
export interface MyCommitmentView {
  readonly commitmentId: string;
  readonly supportNeedId: string;
  readonly state: "accepted" | "reconfirmation_required" | "withdrawn" | "completed" | "cancelled";
  readonly needRevision: number;
  readonly eventRevision?: number;
  readonly origin: "volunteer" | "request" | "proxy";
  readonly acceptedAt: number;
  readonly withdrawnAt?: number;
}

/**
 * Coverage for one need at one moment (NED-012, COV-006, invariant 1).
 *
 * A COUNT of people, derived on read. There is no percentage, no per-hour segment
 * and nothing stored — `isPast` is a function of the clock, not a state anybody set.
 */
export interface CoverageSnapshot {
  readonly supportNeedId: string;
  readonly requiredCapacity: number;
  readonly confirmedCapacity: number;
  readonly status: "needs_someone" | "covered" | "please_recheck" | "done" | "cancelled";
  readonly isPast: boolean;
}

/** A help window, on the wire. Two instants; the browser formats them in the circle's zone. */
export interface WireWindow {
  readonly startsAt: number;
  readonly endsAt: number;
}

/**
 * One thing that changed between the revision somebody agreed to and the one they
 * are being shown (COM-016, plan §10.9).
 *
 * Mirrors the kernel's `MaterialDifference` field for field, in the kernel's
 * spelling. It is mirrored rather than imported because `@herefor/contracts` does not
 * depend on `@herefor/domain-kernel` — the wire shape has to be describable to a
 * client that has no domain code at all — and `contracts.test.ts` pins the two
 * together so the mirror cannot rot silently.
 *
 * `eventLocation` carries NO values on purpose. The revision row stores a
 * fingerprint, not an address; the helper is told the place moved, which is what they
 * need to decide, without a permanent history accumulating real locations (RULE 2).
 */
export type RevisionDifference =
  | { readonly field: "needWindow"; readonly was: WireWindow; readonly now: WireWindow }
  | { readonly field: "requiredCapacity"; readonly was: number; readonly now: number }
  | {
      readonly field: "requiredHelpModes";
      readonly was: readonly string[];
      readonly now: readonly string[];
    }
  | {
      readonly field: "eventWindow";
      readonly was: WireWindow | null;
      readonly now: WireWindow | null;
    }
  | { readonly field: "eventLocation" };

/**
 * One outstanding recheck, with everything the answer depends on.
 *
 * Self-contained by design: a helper answering this must not have to open the need,
 * remember what they agreed to, or compare two screens. `differences` is the "Was" and
 * "Now" the copy asks for, and `needRevision` is what a Yes will bind to — sent back
 * with the answer so the server can refuse if the plan moved again while they read it.
 */
export interface PendingRecheckView {
  readonly commitmentId: string;
  readonly supportNeedId: string;
  readonly label: string;
  /** The need's CURRENT window — what they are being asked about now (NED-002). */
  readonly window: WireWindow;
  readonly needRevision: number;
  /** The revision they originally agreed to. The "Was" side of every difference. */
  readonly agreedNeedRevision: number;
  readonly differences: readonly RevisionDifference[];
}

/**
 * Who has and has not answered, for the organiser (plan §10.9).
 *
 * The other half of the trust loop: the person who made the change has to be able to
 * see the state of the question they caused. Chosen names only.
 */
/**
 * One person in a recheck audience.
 *
 * `commitmentId` is carried on the AWAITING entries only (bead hf-ww3), and only
 * because answering on somebody's behalf needs to name the promise being answered
 * for. Two people holding places on one need are two separate questions, so the need
 * id is not enough; and a person can legitimately appear twice in `declined`, so
 * nothing here may be keyed on a name.
 */
export interface RecheckAudienceMember {
  readonly personId: string;
  readonly displayName: string;
  /** Present on `awaiting` only: the open question, for a proxy answer. */
  readonly commitmentId?: string;
}

export interface RecheckAudienceView {
  readonly supportNeedId: string;
  readonly awaiting: readonly RecheckAudienceMember[];
  readonly reconfirmed: readonly RecheckAudienceMember[];
  readonly declined: readonly RecheckAudienceMember[];
}

export type AcceptNeedResult = Result<CommitmentAccepted, CommitmentRejection>;
export type MyRechecksResult = Result<readonly PendingRecheckView[], CommitmentRejection>;
export type RecheckAudienceResult = Result<RecheckAudienceView, CommitmentRejection>;
export type WithdrawCommitmentResult = Result<{ commitmentId: string }, CommitmentRejection>;
export type ReconfirmCommitmentResult = Result<CommitmentAccepted, CommitmentRejection>;
/** Same shape as the self answer: a proxy recheck is the same claim, differently attributed. */
export type ReconfirmProxyCommitmentResult = ReconfirmCommitmentResult;
export type RecordProxyCommitmentResult = Result<CommitmentAccepted, CommitmentRejection>;
